import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { IconSearch } from '@/ui/icons.tsx';
import { isTv } from '@/lib/native.ts';
import { Focusable } from '@/ui/Focusable.tsx';
import { usePhone } from '@/lib/viewport.ts';

/**
 * THE SEARCH BAR, ALWAYS THERE — not a magnifying glass you have to find.
 *
 * Seerr puts a wide field across the top of every screen and it is the single
 * biggest reason that app feels like it is about finding things. An icon says
 * "search exists somewhere"; a field with words in it says "type here", which
 * is the whole instruction. It also removes a step that was never worth its
 * press: icon → search screen → field → type.
 *
 * ONE FIELD FOR THE WHOLE APP, and this is why it lives in the shell rather
 * than on the search page. A field in the nav AND a field on the results
 * screen is two boxes holding the same string, which is two chances to
 * disagree. So in a browser this IS the search box, and SearchPage renders
 * only answers.
 *
 * ON A TELEVISION IT IS A DOOR, NOT A FIELD — same shape, same words, but
 * pressing it goes to the search screen. A WebView will not raise the system
 * IME for a D-pad, so a text field up here would be a control that looks
 * typeable and cannot be typed into; the on-screen keyboard has to have a
 * screen of its own to live on.
 */
const PLACEHOLDER = 'Search movies, series & sports';

export function NavSearch() {
  const tv = isTv();
  /*
   * A PHONE HAS NO ROOM FOR IT EITHER, for a different reason than the
   * television. There the field cannot be typed into; here it would simply
   * not fit — three destinations, a 460px box and an avatar came to about
   * twice the width of the screen, and a flex row that does not fit does not
   * scroll, it falls off the edge. So on a phone this collapses to the icon
   * it used to be, and the field moves to the search screen where it can be
   * full width.
   */
  const phone = usePhone();
  const compact = tv || phone;
  const navigate = useNavigate();
  const location = useLocation();
  const onSearch = location.pathname === '/search';
  const [params] = useSearchParams();
  const q = onSearch ? params.get('q') ?? '' : '';

  const [text, setText] = useState(q);
  const box = useRef<HTMLInputElement>(null);

  /*
   * The URL is the truth, but only when it changed WITHOUT us.
   *
   * A plain `useEffect(() => setText(q), [q])` fights the debounce below: the
   * URL lands 300ms late, and by then the person has usually typed another
   * letter — which that effect would then wipe. Remembering what we last
   * wrote means we ignore our own echo and obey everything else: the back
   * button, a bookmark, leaving the screen (which empties the box, as it
   * should).
   */
  const pushed = useRef(q);
  useEffect(() => {
    if (q !== pushed.current) { pushed.current = q; setText(q); }
  }, [q]);

  /*
   * Debounced, because each keystroke otherwise costs a round trip to Plex
   * AND one to Seerr. 300ms is about the gap between characters when somebody
   * is actually typing, so it fires when they pause rather than as they go.
   *
   * PUSH ONCE, THEN REPLACE. The first character is a real navigation — BACK
   * from the results should return to the library. Every character after it
   * only updates the query, and pushing those would mean pressing BACK once
   * per letter to get out.
   */
  useEffect(() => {
    if (compact) return;
    if (text === q) return;
    const t = setTimeout(() => {
      pushed.current = text;
      navigate(text ? `/search?q=${encodeURIComponent(text)}` : '/search', { replace: onSearch });
    }, 300);
    return () => clearTimeout(t);
  }, [text, q, compact, onSearch, navigate]);

  // Arriving at the results by any other route — a bookmark, the back button
  // — should still put the cursor where typing goes.
  useEffect(() => { if (!compact && onSearch) box.current?.focus(); }, [compact, onSearch]);

  if (compact) {
    return (
      <Focusable
        id="nav-search"
        className={`nav-search${phone && !tv ? ' nav-search-icon' : ''}`}
        onSelect={() => navigate('/search')}
      >
        <IconSearch size={phone && !tv ? 20 : 18} />
        {/* The words are the invitation, and on a phone there is no room for
            them — the icon has to carry it, as it does in every other app on
            that screen. The label stays for anything reading the page out. */}
        <span className="nav-search-hint">{q || PLACEHOLDER}</span>
      </Focusable>
    );
  }

  return (
    <div className="nav-search">
      {/* The magnifier lives INSIDE the field, as it does everywhere people
          already search. A separate icon beside a box is two things; this is
          one thing that is obviously for typing into. */}
      <IconSearch size={18} />
      <input
        ref={box}
        className="nav-search-box"
        type="search"
        value={text}
        placeholder={PLACEHOLDER}
        aria-label="Search"
        onChange={(e) => setText(e.target.value)}
        data-nav="nav-search"
        tabIndex={0}
      />
    </div>
  );
}
